/**
 * SkipNav — WCAG 2.1 SC 2.4.1 "Bypass Blocks" compliance.
 *
 * Renders a visually-hidden "Skip to main content" link that becomes
 * visible on keyboard focus.  Screen readers and keyboard users can
 * jump directly to the main content region, bypassing the sidebar.
 *
 * Usage:
 *   <SkipNav targetId="main-content" />
 *   <main id="main-content" tabIndex={-1}>...</main>
 */

export interface SkipNavProps {
  /** The `id` of the element to skip to. Defaults to "main-content". */
  targetId?: string;
  /** Link label. Defaults to "Skip to main content". */
  label?: string;
}

export default function SkipNav({
  targetId = 'main-content',
  label = 'Skip to main content',
}: SkipNavProps) {
  return (
    <a
      href={`#${targetId}`}
      className={[
        // Visually hidden — shown only on focus.
        'absolute left-2 top-2 z-50 px-4 py-2 rounded-md',
        'bg-brand-500 text-white text-sm font-medium',
        'translate-y-[-200%] focus:translate-y-0',
        'transition-transform duration-150',
        // Ensure it is reachable via Tab.
        'focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2',
      ].join(' ')}
    >
      {label}
    </a>
  );
}
